import type { TInvestorCategory } from '@/domains/investor/const/InvestorCategories.const'
import type { IDataViewFilters } from '@/domains/investor/components/DataView/DataViewFilters/DataViewFilters.types'
import type { IDataViewFilterOptions, IDataViewHeaderConfig } from '@/domains/investor/components/DataView/DataView.types'
import { FILTERS_DEBOUNCE } from '@/domains/investor/components/DataView/DataView.const'
import { refDebounced } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { EMainCategories } from '@/domains/investor/const/MainCategories.enum'
import { useInvestorFilterOptions } from '@/domains/investor/composables/useInvestorFilterOptions'
import { ESecondCategories } from '@/domains/investor/const/SecondCategories.enum'
import { useSignificantTransactions } from '@/domains/investor/significant-transactions/composables/useSignificantTransactions'
import { ITEMS_PER_PAGE, SIGNIFICANT_TRANSACTIONS_CATEGORIES } from '@/domains/investor/significant-transactions/const/significantTransactions.const'
import {
  SIGNIFICANT_TRANSACTIONS_HEADER_CONFIG,
} from '@/domains/investor/significant-transactions/const/significantTransactionsLabels.const'
import { queryParam, useQueryState } from '@/domains/investor/composables/useQueryState'

export const useSignificantTransactionsDataView = async () => {
  const secondCategory = ref<TInvestorCategory>(ESecondCategories.SIGNIFICANT_TRANSACTION)
  const { page } = useQueryState({ page: queryParam.page() })

  const filters = refDebounced(computed<IDataViewFilters>(() => {
    return {
      mainCategory: [EMainCategories.PRESENTATION, EMainCategories.REPORT],
      secondCategory: SIGNIFICANT_TRANSACTIONS_CATEGORIES,
    }
  }), FILTERS_DEBOUNCE)

  const { categoryOptions: allCategoryOptions } = useInvestorFilterOptions()

  // Keep only the significant transaction category in DataView options.
  const categoryOptions = computed(() =>
    allCategoryOptions.value.filter(option =>
      option.value === ESecondCategories.SIGNIFICANT_TRANSACTION,
    ),
  )

  const filterOptions = computed<IDataViewFilterOptions>(() => ({
    categoryOptions: categoryOptions.value,
  }))

  const { significantTransactions, significantTransactionsColumns, total, isLoading } = await useSignificantTransactions(
    filters,
    page,
    ITEMS_PER_PAGE,
  )

  watch(filters, () => {
    page.value = 1
  })

  const headerConfig = computed<IDataViewHeaderConfig>(() => SIGNIFICANT_TRANSACTIONS_HEADER_CONFIG)

  return {
    secondCategory,
    page,
    itemsPerPage: ITEMS_PER_PAGE,
    filters,
    filterOptions,
    significantTransactions,
    significantTransactionsColumns,
    total,
    isLoading,
    headerConfig,
  }
}
